<template>
  <div>
    <!--    title-->
    <v-row
      style="border-bottom: 1px solid #E5E7EB;"
      class="margin-0"
    >
      <v-col
        class="d-flex justify-start block-title"
      >
        <div
          class="block-title--wrapper"
        >
          {{ item.name ? item.name : defaultEmptyValue }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
      >
        <active-icon
          :cell-data="item.status"
          class="mr-2"
        />

        <table-row-actions-menu
          :data-index="itemIndex"
          :config="actionsConfig"
          :table-data="data"
        />
      </v-col>
    </v-row>

    <!--    visible data section-->
    <v-row
      v-for="(dataObject, dataObjIndex) in getVisibleItemDetails()"
      :key="dataObjIndex"
      class="margin-0"
    >
      <v-col
        class="data-label"
      >
        {{ dataObject.label }}
      </v-col>

      <v-col
        class="d-flex justify-end data-value"
      >
        {{ dataObject.value }}
      </v-col>
    </v-row>

    <!--    hidden data section-->
    <template
      v-if="slidedDown"
    >
      <v-row
        v-for="(dataObject, dataObjIndex) in getAllItemDetails()"
        :key="'hidden_' + dataObjIndex"
        class="margin-0"
      >
        <v-col
          class="data-label"
        >
          {{ dataObject.label }}
        </v-col>

        <v-col
          class="d-flex justify-end data-value"
        >
          <template
            v-if="dataObject.key === 'images'"
          >
            <table-image-icon-with-counter
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else-if="dataObject.key === 'alarm_on'"
          >
            <cell-icon
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else
          >
            {{ dataObject.value }}
          </template>
        </v-col>
      </v-row>
    </template>

    <!--    arrow up/down button for showing more details-->
    <image-field
      :src="slidedDown ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
      class="arrowUpDown"
      @onClick="slidedDown = !slidedDown"
    />

    <information-dialog
      :open-dialog="informationDialogConfig.openDialog"
      :title="informationDialogConfig.title"
      :dialog-message="informationDialogConfig.message"
      :positive-button-text="informationDialogConfig.positiveBtnText"
      :negative-button-text="informationDialogConfig.negativeBtnText"
      :confirm-callback-function="informationDialogConfig.callbackFn"
      @close="$set(informationDialogConfig, 'openDialog', false)"
    />

    <!--      send document to an email dialog-->
    <index-dialog
      v-if="sendDocumentOnEmailDialogConfig.openDialog"
      :open-dialog="sendDocumentOnEmailDialogConfig.openDialog"
      :title="sendDocumentOnEmailDialogConfig.title"
      :positive-button-text="sendDocumentOnEmailDialogConfig.positiveBtnText"
      :negative-button-text="sendDocumentOnEmailDialogConfig.negativeBtnText"
      :confirm-callback-function="sendDocumentOnEmailDialogConfig.callbackFn"
      @close="$set(sendDocumentOnEmailDialogConfig, 'openDialog', false)"
    >
      <send-document-on-email
        :validation-errors="documentEmailValidationErrors"
        @update-data="onDocumentEmailDataUpdate"
        @clear-validation-errors="key => $delete(documentEmailValidationErrors, key)"
        @remove-all-validation-errors="documentEmailValidationErrors = {}"
      />
    </index-dialog>

    <images-preview-dialog
      v-if="imagesPreviewDialogConfig.show"
      :show-dialog="imagesPreviewDialogConfig.show"
      :dialog-title="imagesPreviewDialogConfig.title"
      :images="imagesPreviewDialogConfig.images"
      :field="imagesPreviewDialogConfig.field"
      :remove-callback="imagesPreviewDialogConfig.removeCallback"
      :remove-api-config="imagesPreviewDialogConfig.removeApiConfig"
      @close="imagesPreviewDialogConfig.show = false"
    />
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import ActiveIcon from '../../components/main-table/ActiveIcon.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { dateService, FORMATS } from '@/global/services/date'
import TableImageIconWithCounter from '../../components/TableImageIconWithCounter.vue'
import CellIcon from '../../components/main-table/CellIcon.vue'
import SendDocumentOnEmail from '../../components/SendDocumentOnEmail.vue'
import IndexDialog from '@/global/components/dialogs/IndexDialog.vue'
import previewFiles from '../../helpers/previewFiles'
import ImagesPreviewDialog from '@/global/components/dialogs/ImagesPreviewDialog.vue'
import vehicleDocumentMixin from '../../mixins/vehicleDocumentMixin'
import { showDetailsAction, activationAction } from '@/global/components/table/helpers/tableActions'

const DEFAULT_EMPTY_VALUE = '--'
export default {
  name: 'DocumentsGridBlock',

  components: {
    ImagesPreviewDialog,
    IndexDialog,
    SendDocumentOnEmail,
    CellIcon,
    TableImageIconWithCounter,
    InformationDialog,
    TableRowActionsMenu,
    ActiveIcon,
    ImageField
  },

  mixins: [
    previewFiles,
    vehicleDocumentMixin
  ],

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      slidedDown: false,
      informationDialogConfig: {
        openDialog: false,
        message: this.$t('fleet/vehicle.confirm_delete'),
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      },
      sendDocumentOnEmailDialogConfig: {
        openDialog: false,
        title: this.$t('fleet/vehicle/documents_tab.email_dialog_title'),
        positiveBtnText: this.$t('base.send'),
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => this.sendEmail()
      },
      documentEmailId: null,
      documentEmailFormData: {},
      documentEmailValidationErrors: {},
      documentValidationErrors: {},
      imagesPreviewDialogConfig: {
        show: false,
        title: this.$t('fleet/vehicle_comment.images'),
        field: {},
        images: [],
        removeApiConfig: {
          module: 'fleet',
          route: ''
        },
        removeCallback: (event) => this.removeImageCallback(event)
      },
      defaultEmptyValue: DEFAULT_EMPTY_VALUE
    }
  },

  computed: {
    actionsConfig () {
      return {
        tableConfig: {
          actionsConfig: [
            showDetailsAction(this.$t, (event) => this.showDocumentEdit(event)),
            {
              label: this.$t('fleet/vehicle/documents_tab.send_to_email_button_label'),
              src: '/img/icons/email-send-action-icon.svg',
              click: (event) => this.onDocumentSendEmail(event)
            },
            activationAction(this.$t, (event) => this.onDocumentActivation(event), (event) => !!event?.data?.status)
          ]
        }
      }
    }
  },

  methods: {
    getVisibleItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_document.identification_string'),
          value: this.item?.identification_string || DEFAULT_EMPTY_VALUE
        },
        {
          key: 'status',
          label: this.$t('fleet/vehicle_document.issued'),
          value: this.item?.issued ? dateService.formatFromUtc(this.item.issued, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          key: 'issued_by',
          label: this.$t('fleet/vehicle_document.issued_by'),
          value: this.item?.issued_by || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_document.valid_until'),
          value: this.item?.valid_until ? dateService.formatFromUtc(this.item.valid_until, FORMATS.date) : DEFAULT_EMPTY_VALUE
        }
      ]
    },

    getAllItemDetails () {
      return [
        {
          key: 'images',
          label: this.$t('fleet/vehicle_document.images'),
          value: this.item?.images || [],
          onClick: (event) => this.showImages(event, {
            module: 'fleet',
            route: 'vehicle-document/remove-media/'
          }, 'documents')
        },
        {
          label: this.$t('fleet/vehicle_document.note'),
          value: this.item?.note || DEFAULT_EMPTY_VALUE
        },
        {
          key: 'alarm_on',
          label: this.$t('fleet/vehicle_document.alarm_on'),
          value: this.item?.alarm_on,
          src: this.item?.alarm_on === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
        }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.block-title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.block-title--wrapper {
  display: flex;
  height: 48px;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
}

.data-value {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
